import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { PageTitle, Card, F, Toggle, SingleImage } from "./AdminUI";

export default function SettingsAdmin() {
  const qc = useQueryClient();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { api.get("/admin/settings").then((r) => setF(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/admin/settings", f); toast.success("Impostazioni salvate"); qc.invalidateQueries({ queryKey: ["settings"] }); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  if (!f) return <div className="text-sm text-stone">Caricamento…</div>;
  return (
    <form onSubmit={save} data-testid="admin-settings-form">
      <PageTitle title="Impostazioni" subtitle="Dati aziendali, contatti, social, SEO e note legali" action={<button type="submit" disabled={busy} data-testid="admin-settings-save" className="btn-primary !py-2.5 !px-5 disabled:opacity-50">{busy ? "Salvo…" : "Salva"}</button>} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="space-y-5">
          <h2 className="text-sm font-medium">Azienda e contatti</h2>
          <F label="Ragione sociale" name="company_name" value={f.company_name} onChange={set} />
          <F label="Email" name="email" value={f.email} onChange={set} />
          <F label="PEC" name="pec" value={f.pec} onChange={set} />
          <F label="Email per le notifiche delle richieste" name="notification_email" value={f.notification_email} onChange={set} hint="Riceve un'email a ogni nuovo contatto, proposta immobile, richiesta investitore o anteprima libro" />
          <F label={f.resend_api_key_set ? "API key Resend (già salvata — inserisci solo per sostituirla)" : "API key Resend (campo protetto)"} name="resend_api_key" type="password" value={f.resend_api_key} onChange={set} hint="Crea la chiave su resend.com/api-keys (inizia con re_). Salvata solo sul server, mai mostrata" />
          <F label="Mittente email" name="sender_email" value={f.sender_email} onChange={set} hint="Con dominio verificato su Resend usa es. notifiche@homehaven.it; altrimenti onboarding@resend.dev (invia solo all'email del tuo account Resend)" />
          <F label="Telefono (visualizzato)" name="phone" value={f.phone} onChange={set} />
          <F label="Indirizzo sede" name="address" value={f.address} onChange={set} hint="Lascia vuoto se non hai una sede aperta al pubblico" />
        </Card>
        <Card className="space-y-5">
          <h2 className="text-sm font-medium">WhatsApp e social</h2>
          <F label="Numero WhatsApp (formato internazionale, solo cifre)" name="whatsapp_number" value={f.whatsapp_number} onChange={set} hint="Es. 393288565455" />
          <F label="Messaggio WhatsApp precompilato" name="whatsapp_message" as="textarea" rows={2} value={f.whatsapp_message} onChange={set} />
          <F label="Instagram" name="instagram" value={f.instagram} onChange={set} />
          <F label="LinkedIn" name="linkedin" value={f.linkedin} onChange={set} />
          <F label="Facebook" name="facebook" value={f.facebook} onChange={set} />
        </Card>
        <Card className="space-y-5">
          <h2 className="text-sm font-medium">Google Maps</h2>
          <Toggle label="Mostra mappa nella pagina Contatti" name="show_map" checked={f.show_map} onChange={set} />
          <F label="URL embed Google Maps" name="maps_embed_url" value={f.maps_embed_url} onChange={set} hint="Da Google Maps → Condividi → Incorpora mappa → copia solo l'URL src" />
        </Card>
        <Card className="space-y-5">
          <h2 className="text-sm font-medium">SEO e Analytics</h2>
          <F label="ID Google Analytics 4" name="ga4_id" value={f.ga4_id} onChange={set} hint="Es. G-XXXXXXXXXX — attivato solo dopo il consenso cookie" />
          <F label="Titolo sito" name="site_title" value={f.site_title} onChange={set} />
          <F label="Descrizione sito" name="site_description" as="textarea" rows={3} value={f.site_description} onChange={set} />
          <SingleImage label="Immagine hero homepage" name="hero_image" value={f.hero_image} onChange={set} folder="site" />
        </Card>
        <Card className="space-y-5">
          <h2 className="text-sm font-medium">Dati legali (obbligatori per legge sul sito — art. 2250 c.c.)</h2>
          <F label="Partita IVA" name="vat_number" value={f.vat_number} onChange={set} hint="Es. IT01234567890" />
          <F label="Codice fiscale (se diverso dalla P.IVA)" name="fiscal_code" value={f.fiscal_code} onChange={set} />
          <F label="Sede legale" name="registered_office" value={f.registered_office} onChange={set} hint="Via, n., CAP, Città" />
          <F label="Registro Imprese / REA" name="rea_number" value={f.rea_number} onChange={set} hint="Es. Messina, REA ME-123456" />
          <F label="Capitale sociale" name="share_capital" value={f.share_capital} onChange={set} hint="Es. € 10.000,00 i.v." />
          <F label="Foro competente (città)" name="court_city" value={f.court_city} onChange={set} />
        </Card>
        <Card className="space-y-5 lg:col-span-2">
          <h2 className="text-sm font-medium">Nota legale investitori</h2>
          <F label="Disclaimer" name="investor_disclaimer" as="textarea" rows={5} value={f.investor_disclaimer} onChange={set} />
        </Card>
      </div>
    </form>
  );
}
